import { createPasskeyRegistration, getAuthenticatorPrf, importSymmetricKey } from '@tadpoles/web-credentials';
import { createRandomDataKey, unwrapDataKey, wrapDataKey } from './crypto.js';
const ActiveEnrollmentBindings = new Set();
const CurrentWrappedKeyVersion = 1;
function createUnlockRequest(request, credentialId) {
    return {
        ...request,
        allowCredentials: [
            {
                id: credentialId,
                type: 'public-key'
            }
        ]
    };
}
function serializeBinding(binding) {
    switch (binding.kind) {
        case 'local-device':
            return `${binding.kind}:${binding.userId}:${binding.deviceId}`;
        case 'server-passkey':
            return `${binding.kind}:${binding.userId}`;
    }
}
function assertLocalEnrollmentBinding(binding) {
    if (binding.kind !== 'local-device') {
        throw new Error('Local protection enrollment currently supports only local-device bindings.');
    }
}
function getEnrollmentKeyVersion(keyVersion) {
    const resolvedKeyVersion = keyVersion ?? CurrentWrappedKeyVersion;
    if (resolvedKeyVersion !== CurrentWrappedKeyVersion) {
        throw new Error(`Unsupported local protection key version "${resolvedKeyVersion}".`);
    }
    return resolvedKeyVersion;
}
function createCredentialRecord(binding, credentialId) {
    return {
        binding,
        createdAt: Date.now(),
        credentialId
    };
}
function createWrappedKeyRecord(binding, wrappedKey, keyVersion) {
    return {
        binding,
        createdAt: Date.now(),
        keyVersion,
        wrapAlgorithm: 'AES-GCM',
        wrapNonce: wrappedKey.wrapNonce,
        wrappedKey: wrappedKey.wrappedKey
    };
}
async function getStoredCredentialRecord(store, binding) {
    const credentialRecord = await store.getCredential(binding);
    if (credentialRecord === null) {
        throw new Error('No stored credential record exists for the requested binding.');
    }
    return credentialRecord;
}
async function getStoredWrappedKeyRecord(store, binding) {
    const wrappedKeyRecord = await store.getWrappedKey(binding);
    if (wrappedKeyRecord === null) {
        throw new Error('No stored wrapped key record exists for the requested binding.');
    }
    return wrappedKeyRecord;
}
async function getStoredEnrollment(store, binding) {
    const [credentialRecord, wrappedKeyRecord] = await Promise.all([
        getStoredCredentialRecord(store, binding),
        getStoredWrappedKeyRecord(store, binding)
    ]);
    return {
        credentialRecord,
        wrappedKeyRecord
    };
}
async function assertBindingNotEnrolled(store, binding) {
    const [credentialRecord, wrappedKeyRecord] = await Promise.all([
        store.getCredential(binding),
        store.getWrappedKey(binding)
    ]);
    if (credentialRecord !== null || wrappedKeyRecord !== null) {
        throw new Error('Local protection is already enrolled for the requested binding.');
    }
}
async function deriveWrappingKey(credentialId, prf, unlock) {
    const result = await getAuthenticatorPrf({
        prf,
        publicKey: createUnlockRequest(unlock, credentialId)
    });
    return await importSymmetricKey({
        keyMaterial: result.prf.first,
        usages: ['wrapKey', 'unwrapKey']
    });
}
async function createEnrollmentArtifacts(request, keyVersion) {
    const registration = await createPasskeyRegistration(request.registration);
    const credentialRecord = createCredentialRecord(request.binding, registration.id);
    const wrappingKey = await deriveWrappingKey(credentialRecord.credentialId, request.prf, request.unlock);
    const dataKey = await createRandomDataKey();
    const wrappedKey = await wrapDataKey(wrappingKey, dataKey);
    const wrappedKeyRecord = createWrappedKeyRecord(request.binding, wrappedKey, keyVersion);
    return {
        credentialRecord,
        dataKey,
        registration,
        wrappedKeyRecord
    };
}
async function persistEnrollment(store, artifacts) {
    let stored = false;
    try {
        stored = await store.putEnrollmentIfAbsent({
            credentialRecord: artifacts.credentialRecord,
            wrappedKeyRecord: artifacts.wrappedKeyRecord
        });
    }
    catch (error) {
        throw new Error('Failed to persist local protection enrollment after credential creation. A credential may have been created but not enrolled.', {
            cause: error
        });
    }
    if (!stored) {
        throw new Error('Local protection enrollment could not be committed. A credential may have been created but not enrolled.');
    }
}
async function enrollAndUnlockLocalProtectionInternal(store, request, options) {
    assertLocalEnrollmentBinding(request.binding);
    const bindingKey = serializeBinding(request.binding);
    const keyVersion = getEnrollmentKeyVersion(request.keyVersion);
    if (ActiveEnrollmentBindings.has(bindingKey)) {
        throw new Error('Local protection enrollment is already in progress for the requested binding.');
    }
    ActiveEnrollmentBindings.add(bindingKey);
    try {
        if (options.assertNotEnrolled) {
            await assertBindingNotEnrolled(store, request.binding);
        }
        const artifacts = await createEnrollmentArtifacts(request, keyVersion);
        await persistEnrollment(store, artifacts);
        return {
            credentialRecord: artifacts.credentialRecord,
            key: artifacts.dataKey,
            registration: artifacts.registration,
            wrappedKeyRecord: artifacts.wrappedKeyRecord
        };
    }
    finally {
        ActiveEnrollmentBindings.delete(bindingKey);
    }
}
export async function enrollAndUnlockLocalProtection(store, request) {
    return await enrollAndUnlockLocalProtectionInternal(store, request, {
        assertNotEnrolled: false
    });
}
export async function enrollLocalProtection(store, request) {
    const result = await enrollAndUnlockLocalProtectionInternal(store, request, {
        assertNotEnrolled: true
    });
    return {
        credentialRecord: result.credentialRecord,
        registration: result.registration,
        wrappedKeyRecord: result.wrappedKeyRecord
    };
}
export async function unlockLocalDataKeyFromEnrollment(enrollment, request) {
    const wrappingKey = await deriveWrappingKey(enrollment.credentialRecord.credentialId, request.prf, request.unlock);
    return await unwrapDataKey(wrappingKey, enrollment.wrappedKeyRecord);
}
export async function unlockLocalDataKey(store, request) {
    const enrollment = await getStoredEnrollment(store, request.binding);
    return await unlockLocalDataKeyFromEnrollment(enrollment, request);
}
